import { useCallback, useMemo } from "react";
import type { QueryClient } from "react-query";
import { useMutation, useQuery } from "react-query";

import type { AddressStored, ICountries } from "@jti/fe-common";
import { addCountryToAddress } from "@jti/fe-common";

import { MUTATION_KEYS, QUERY_KEYS } from "../../../constants/queryKeys";
import { CasAddressesApi, CasProfileApi, convertCasAddressToStoredAddress } from "../../../services";

type AddressObjectType = {
  defaultShippingAddress?: AddressStored;
  addresses: AddressStored[];
};

type AddressesOperationType = {
  addAddress: (address: AddressStored) => Promise<{
    addresses: AddressStored[];
    address: AddressStored;
  }>;
  editAddress: (
    addressId: string,
    address: AddressStored
  ) => Promise<{
    addresses: AddressStored[];
    address?: AddressStored;
  }>;
  deleteAddress: (addressId: string) => Promise<void>;
  setDefaultShipping: (addressId: string) => Promise<{
    addresses: AddressStored[];
    address?: AddressStored;
  }>;
};

type AddressesType = AddressesOperationType & {
  data: AddressObjectType;
  isLoadingData: boolean;
};

type UseAddressesOptions = {
  countries: ICountries[];
  queryClient: QueryClient;
  isCountriesLoading?: boolean;
  onAddressChange?: (address?: AddressStored) => void;
};

export const useAddressOperations = (
  queryClient: QueryClient,
  onAddressChange?: (address?: AddressStored) => void
): AddressesOperationType => {
  const casAddressesApi = CasAddressesApi.init();

  const updateMutation = useMutation(
    MUTATION_KEYS.setDefaultShipping,
    (addressId: string) => casAddressesApi.setDefaultShippingAddress(addressId),
    {
      onSuccess: () => {
        onAddressChange?.();
        queryClient.invalidateQueries(QUERY_KEYS.profile);
      },
    }
  );

  const addAddressMutation = useMutation(
    MUTATION_KEYS.createAddress,
    (address: AddressStored) => casAddressesApi.createAddress(address),
    {
      onSuccess: (_response, address) => {
        if (address.isDefaultShipping) {
          onAddressChange?.(address);
        }
        queryClient.invalidateQueries(QUERY_KEYS.profile);
      },
    }
  );

  const editAddressMutation = useMutation(
    MUTATION_KEYS.updateAddress,
    ({ addressId, address }: { addressId: string; address: AddressStored }) =>
      casAddressesApi.updateAddress(addressId, address),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.profile);
      },
    }
  );

  const deleteAddressMutation = useMutation(
    MUTATION_KEYS.deleteAddress,
    (addressId: string) => casAddressesApi.deleteAddress(addressId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.profile);
      },
    }
  );

  const setDefaultShipping = useCallback(
    async (addressId: string) => await updateMutation.mutateAsync(addressId),
    [updateMutation]
  );

  const addAddress = useCallback(
    async (address: AddressStored) => await addAddressMutation.mutateAsync(address),
    [addAddressMutation]
  );

  const editAddress = useCallback(
    async (addressId: string, address: AddressStored) => await editAddressMutation.mutateAsync({ addressId, address }),
    [editAddressMutation]
  );

  const deleteAddress = useCallback(
    async (addressId: string) => await deleteAddressMutation.mutateAsync(addressId),
    [deleteAddressMutation]
  );

  return {
    addAddress,
    editAddress,
    deleteAddress,
    setDefaultShipping,
  };
};

export const useAddresses = (options: UseAddressesOptions): AddressesType => {
  const { countries, queryClient, onAddressChange, isCountriesLoading = false } = options;
  const { addAddress, editAddress, deleteAddress, setDefaultShipping } = useAddressOperations(
    queryClient,
    onAddressChange
  );
  const casProfileApi = CasProfileApi.init();

  const { isLoading, data: addressData } = useQuery(QUERY_KEYS.profile, () => casProfileApi.getProfile(), {
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const transformedCasAddresses = {
    addresses: (addressData?.addresses || []).map((address) => convertCasAddressToStoredAddress(address)),
  };

  const data: AddressObjectType = useMemo(() => {
    const addresses = transformedCasAddresses?.addresses || [];

    return addresses.reduce(
      (acc, address) => {
        const addressFull = addCountryToAddress(address, countries);
        if (address.isDefaultShipping) {
          acc.defaultShippingAddress = addressFull;
        } else {
          acc.addresses.push(addressFull);
        }
        return acc;
      },
      { addresses: [] } as AddressObjectType
    );
  }, [transformedCasAddresses?.addresses, countries]);

  return {
    addAddress,
    editAddress,
    deleteAddress,
    setDefaultShipping,
    data,
    isLoadingData: isLoading || isCountriesLoading,
  };
};
